/**
 * Nutmil Theme Global Cart API
 * Handles cart state, AJAX calls, and header badge updates.
 */
class CartAPI {
  /**
   * Add a product variant to the cart.
   * @param {number|string} variantId - Variant ID to add.
   * @param {number} quantity - Quantity to add.
   * @param {Object|null} properties - Optional line item properties (e.g. { "Cashew Grade": "Cashew 240" }).
   */
  static async add(variantId, quantity = 1, properties = null) {
    try {
      const item = { id: variantId, quantity };
      if (properties && Object.keys(properties).length > 0) {
        item.properties = properties;
      }
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      await this.updateCartCount();
      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Change quantity of a specific item in the cart.
   */
  static async change(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      this.updateCartCount(data);
      return data;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  /**
   * Fetch full cart object.
   */
  static async getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Network response was not ok');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  }

  /**
   * Update the Cart Count badge in the header.
   */
  static async updateCartCount(cartData = null) {
    const data = cartData || await this.getCart();
    const countElements = document.querySelectorAll('#CartCount');
    countElements.forEach(el => {
      el.textContent = data.item_count;
      el.style.display = data.item_count > 0 ? 'flex' : 'none';
    });
  }

  /**
   * Format an amount in cents using the store's money format (Shopify Admin currency).
   * Mirrors Shopify's canonical Shopify.formatMoney so the correct currency symbol
   * (e.g. ₹) is always used instead of a hardcoded one.
   */
  static formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    const formatString = format || window.themeMoneyFormat || '{{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    };

    let value = '';
    const match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }
  /**
   * Helper for Quick Add buttons
   */
  static async handleQuickAdd(button, variantId) {
    if (!button || button.disabled) return;
    const btnText = button.querySelector('.btn-text') || button;
    const originalText = btnText.innerHTML;
    
    button.disabled = true;
    btnText.textContent = 'Adding...';
    
    try {
      await this.add(variantId, 1);
      btnText.textContent = 'Added!';
      setTimeout(() => {
        btnText.innerHTML = originalText;
        button.disabled = false;
      }, 2000);
    } catch (err) {
      btnText.textContent = 'Error';
      setTimeout(() => {
        btnText.innerHTML = originalText;
        button.disabled = false;
      }, 2000);
    }
  }
}

// Initialize Shopify routes if not present
window.Shopify = window.Shopify || {};
window.Shopify.routes = window.Shopify.routes || { root: '/' };

// Attach to window for global access
window.CartAPI = CartAPI;
